import { types, getParent, flow } from "mobx-state-tree"

import Plate from "~/stores/models/Plate"

import * as PlatesService from "~/services/plates"
import Network from "./Network"

const UserPlatesBaseModel = types
  .model("UserPlatesBaseModel", {
    plates: types.array(Plate),
    platesService: types.optional(Network, {}),
    temp: types.safeReference(Plate)
  })
  .views(self => ({
    get sessionToken() {
      return getParent(self).auth.user.sessionToken
    },
    get isLoading() {
      const singlePlatesLoading = self.plates.reduce((isLoading, plate) => {
        if (isLoading || plate.isLoading) return true
        return false
      }, false)
      return self.platesService.isLoading || singlePlatesLoading
    }
  }))
  .actions(self => ({
    resetTempPlate() {
      if (self.temp) {
        self.removePlate(self.temp)
        self.temp = undefined
      }
    },
    removePlate(newPlate) {
      self.plates = self.plates.filter(plate => {
        return plate.fakeId !== newPlate.fakeId
      })
    },
    setPlates(plates) {
      self.plates = plates
      self.plates.forEach(plate => plate.setLastUpdate())
    },
    addPlate(plate) {
      // if (self.temp) {
      //   if (self.temp.isPersisted) {
      //     // if the plate sitting in temp was already saved clear it
      //     self.temp = undefined
      //   } else {
      //     // if it was not saved, return it
      //     return self.temp
      //   }
      // }
      const newPlate = Plate.create(plate)
      self.plates.push(newPlate)
      self.temp = newPlate
      return newPlate
    },
    loadIfNecessary: flow(function* loadIfNecessaryGen() {
      if (!self.sessionToken) return false
      if (self.platesService.isLoading || self.platesService.isComplete) {
        return true
      }
      yield self.fetchAll()
    }),
    findPlate(plateNumber, state) {
      return self.plates.find(plate => {
        const samePlateNumber =
          plate.plateNumber.trim().toLowerCase() ===
          plateNumber.trim().toLowerCase()
        const sameState = plate.state === state
        return samePlateNumber && sameState
      })
    },
    updateTicketGuardian: flow(function* updateTicketGuardianGen(plateId, action) {
      let success = false
      yield self.fetchAll()
      
      try {
        // Find the plate by ID
        const targetPlate = self.plates.find(plate => plate.objectId === plateId)
        
        if (!targetPlate) {
          console.error("Plate not found", plateId)
          throw new Error("Plate not found")
        }

        // Set isAutoDispute to false
        targetPlate.setAutoDispute(action === 'enable')
        
        // Save the changes
        success = yield targetPlate.update()
        
        if (success) {
          // Refresh the plates list
          yield self.fetchAll()
        }
      } catch (error) {
        if (process.env.WINIT_ENV !== "test") {
          console.error("Error updating ticket guardian", error)
        }
        success = false
      }
      return success
    }),
    fetchAll: flow(function* fetch() {
      let success = false
      try {
        if (!self.sessionToken) return false // user is not signed in
        if (self.platesService.isLoading) {
          return true
        }
        self.platesService.requestStart()
        const plates = yield PlatesService.fetchAllPlates(self.sessionToken)
        self.setPlates(plates)
        success = true
        self.platesService.requestComplete()
      } catch (error) {
        if (process.env.WINIT_ENV !== "test") {
          console.error("error fetching all of a users plates", error)
        }
        success = false
        self.platesService.requestError(error)
      }
      return success
    }),
    updateAll: flow(function* updateAll() {
      let success = false
      try {
        if (self.platesService.isLoading) return true
        self.platesService.requestStart()
        yield Promise.all(
          self.plates.map(plate => {
            return plate.update()
          })
        )
        success = true
        self.platesService.requestComplete()
      } catch (error) {
        if (process.env.WINIT_ENV !== "test") {
          console.error("error updating all of a users plates", error)
        }
        success = false
        self.platesService.requestError(error)
      }
      return success
    })
  }))

export default UserPlatesBaseModel
